import { useState, useEffect, useCallback, useMemo } from 'react';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { Card, Container, IconButton } from '@mui/material';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

// components
import Iconify from '../../../components/iconify';
import AssetHeader from './AssetHeader';
import ActionsPopover from './ActionsPopover';
import DeleteModal from './DeleteModal';
import AssetFormModal from './AssetFormModal';
import Loading from '../../../layouts/loading';

// styles
import 'react-toastify/dist/ReactToastify.css';

import { checkComponentPermission } from '../../../utils/share';
import { boldHeader, ASSET_LIST_TABLE_COLUMNS, ASSET_LIST_TABLE_DEFAULT_PROPS } from '../constants/assetConstants';
import BreadCrumb from '../../../components/breadcrumb/BreadCrumbNavigation';
import TableWrapper from '../../../common/TableWrapper';
import {
  createAsset,
  deleteAsset,
  getAllAssets,
  getAllFilteredAssets,
  getUsers,
  updateAsset,
} from '../../../services/assetService';

// ----------------------------------------------------------------------

const canEditAsset = checkComponentPermission('EDIT_USER');
const canDeleteAsset = checkComponentPermission('DELETE_USER');
const showAction = canEditAsset || canDeleteAsset;

// ----------------------------------------------------------------------

function AssetTable() {
  const userId = useSelector((state) => state?.login?.user.id || null);
  const [openActions, setOpenActions] = useState(null);
  const [openFormModal, setOpenFormModal] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [action, setAction] = useState('');
  const [selectedRow, setSelectedRow] = useState([]);
  const [users, setUsers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [rowCount, setRowCount] = useState(0);

  // 🔹 Fetch all assets & users once on mount (frontend-only mode)
  const fetchData = async () => {
    try {
      setIsLoading(true);

      const sortBy = sorting[0]?.id || 'createdAt';
      const sortOrder = sorting[0]?.desc ? 'DESC' : 'ASC';
      const page = pagination.pageIndex + 1;
      const limit = pagination.pageSize;

      const res = await getAllFilteredAssets({
        keyword: globalFilter,
        sortBy,
        sortOrder,
        page,
        limit,
      });
      
      setAssets(res.data.data.rows);
      setRowCount(res.data.data.total);
    } catch (error) {
      toast.error('Error loading assets');
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    const users=async()=>{
      const user = await getUsers();
      setUsers(user?.data?.data);
    }
    users();
  }, []);
  useEffect(() => {
    fetchData();
  }, [globalFilter, sorting, pagination]);

  const handleCreateClick = () => {
    setAction('create');
    setOpenFormModal(true);
  };

  const handleFormModalClose = useCallback(() => {
    setOpenFormModal(false);
  }, []);

  const handleOpenMenu = (event, row) => {
    setOpenActions(event.currentTarget);
    setSelectedRow(row);
  };

  const handleCloseMenu = () => {
    setOpenActions(null);
  };

  const handleEditClick = useCallback(() => {
    setAction('edit');
    setOpenFormModal(true);
    setOpenActions(null);
  }, []);

  const handleDeleteClick = useCallback(() => {
    setOpenDelete(true);
    setOpenActions(null);
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setOpenDelete(false);
  }, []);

  const handleDelete = useCallback(async () => {
    try {
      setIsLoading(true);
      await deleteAsset(selectedRow.original.id);
      toast.success('Asset deleted successfully');
      setOpenDelete(false);

      // Refresh local list
      await fetchData();
    } catch (error) {
      console.error('Error deleting asset:', error);
      toast.error('Error deleting asset');
    } finally {
      setIsLoading(false);
    }
  }, [selectedRow]);

  const handleFormModalSubmit = useCallback(
    async (formData) => {
      try {
        setIsLoading(true);
        if (action === 'create') {
          await createAsset(formData);
        } else {
          await updateAsset(selectedRow.original.id, formData);
        }
        toast.success(`Asset ${action === 'create' ? 'created' : 'updated'} successfully`);
        setOpenFormModal(false);

        // Refresh local list
        await fetchData();
      } catch (error) {
        console.error(`Error ${action === 'create' ? 'creating' : 'updating'} asset:`, error);
        toast.error(`Error ${action === 'create' ? 'creating' : 'updating'} asset`);
      } finally {
        setIsLoading(false);
      }
    },
    [action, selectedRow]
  );

  // ----------------------------------------------------------------------
  // ✅ Table setup: client-side pagination, sorting, and search
  // ----------------------------------------------------------------------
  const columns = useMemo(
    () => [
      ...ASSET_LIST_TABLE_COLUMNS,
      ...(showAction
        ? [
            {
              id: 'actions',
              header: 'Actions',
              Header: boldHeader('#454F5B'),
              enableColumnFilter: false,
              enableSorting: false,
              size: 50,
              Cell: ({ row }) => (
                <IconButton
                  size="small"
                  onClick={(event) => handleOpenMenu(event, row)}
                  data-testid="action-buttons-container"
                >
                  <Iconify icon="eva:more-vertical-fill" />
                </IconButton>
              ),
            },
          ]
        : []),
    ],
    [showAction]
  );

  const table = useMaterialReactTable({
    columns,
    data: assets,
    rowCount,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    manualSorting: true,
    manualPagination: true,
    enablePagination: true,
    enableSorting: true,
    enableGlobalFilter: true,
    enableColumnFilters: true,
    enableColumnActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    globalFilterFn: 'contains',
    state: {
      globalFilter,
      sorting,
      pagination,
    },
    initialState: {
      pagination: { pageSize: 10, pageIndex: 0 },
    },
    muiTableBodyRowProps: () => ({
      sx: { backgroundColor: '#fff' },
    }),
    ...ASSET_LIST_TABLE_DEFAULT_PROPS,
    muiPaginationProps: {
      color: 'primary',
      variant: 'outlined',
      shape: 'rounded',
    },
  });

  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: 'Assets', path: '' },
  ];

  return (
    <>
      <BreadCrumb breadcrumbs={breadcrumbs} />
      <Container maxWidth="4xl">
        <Loading open={isLoading} />

        <AssetHeader onCreateClick={handleCreateClick} />

        <Card>
          <TableWrapper>
            <MaterialReactTable table={table} />
          </TableWrapper>
        </Card>
      </Container>

      <ActionsPopover
        open={openActions}
        onClose={handleCloseMenu}
        onEditClick={handleEditClick}
        onDeleteClick={handleDeleteClick}
      />

      {openFormModal && (
        <AssetFormModal
          open
          onClose={handleFormModalClose}
          selectedRow={selectedRow}
          action={action}
          onSubmit={handleFormModalSubmit}
          users={users}
        />
      )}

      <DeleteModal open={openDelete} onClose={handleDeleteModalClose} onDelete={handleDelete} />
    </>
  );
}

export default AssetTable;
